import Api, { tmdb } from 'services/BaseService'
import { sanityClient, sanityClientFresh } from 'services/SanityClient'
// Interfaces
import { IGetListPayload, IDetailPayload } from '@/interfaces/IPayloads'
import axios from 'axios'
import { urlFor } from 'services/SanityImage'

// Get streaming data API from WatchMode
export const fetchMovieSources = async (tmdbId: string) => {
  if (!tmdbId) {
    throw new Error('No tmdbId provided to fetchMovieSources')
  }

  const baseURL = 'https://api.watchmode.com'
  const endpoint = `/v1/title/movie-${tmdbId}/sources/`
  const params = {
    // apiKey: import.meta.env.VITE_WATCHMODE_API_KEY,
    apiKey: 'f0Xy1XtHAg6C6McbQxONiH5afgRV7zXb7J8urVTk',
    regions: 'US',
  }

  try {
    const response = await axios.get(`${baseURL}${endpoint}`, { params })
    // console.log('Streaming data response:', response.data)
    return response.data
  } catch (error) {
    throw new Error('Failed to fetch sources')
  }
}

/**
 * Cast & crew live in Sanity as arrays of castMember/crewMember objects that
 * reference a `person` document (name, slug, headshot). Older schemas name the
 * arrays `castMembers`/`crewMembers`, newer ones `cast`/`crew`, so coalesce both.
 */
const CAST_CREW_PROJECTION = `
      "cast": coalesce(cast, castMembers)[]{
        characterName,
        "person": person->{ name, "slug": slug.current, image }
      },
      "crew": coalesce(crew, crewMembers)[]{
        department,
        job,
        "person": person->{ name, "slug": slug.current, image }
      }`

const personImageUrl = (image: any) =>
  image ? urlFor(image).width(276).height(350).fit('crop').url() : ''

/** Normalize Sanity cast members into the TMDB credit shape the UI already renders. */
export const normalizeSanityCast = (cast: any[] | undefined) =>
  (cast ?? [])
    .filter((member) => member?.person?.name)
    .map((member, index) => ({
      id: member.person.slug || `${member.person.name}-${index}`,
      name: member.person.name,
      character: member.characterName || '',
      profile_path: '',
      profile_url: personImageUrl(member.person.image),
      order: index,
    }))

/** Same, for crew — the job (e.g. "Director") takes the place of the character name. */
export const normalizeSanityCrew = (crew: any[] | undefined) =>
  (crew ?? [])
    .filter((member) => member?.person?.name)
    .map((member, index) => ({
      id: member.person.slug || `${member.person.name}-${index}`,
      name: member.person.name,
      character: member.job || member.department || '',
      job: member.job || '',
      department: member.department || '',
      profile_path: '',
      profile_url: personImageUrl(member.person.image),
      order: index,
    }))

// BFE Master Movie - Helper Types & Functions
type ReleaseScope = 'domestic' | 'international' | 'both'

const scopesFor = (scope: ReleaseScope) =>
  scope === 'domestic'
    ? ['domestic', 'both']
    : scope === 'international'
      ? ['international', 'both']
      : ['domestic', 'international', 'both']

// get movies by genre OR title keyword
export const getFamilyAndHolidayMovies = async (
  scope: 'domestic' | 'international' | 'both' = 'both',
  limit = 30
) => {
  const scopes = scopesFor(scope)

  const query = `*[
  _type == "bfe_master_movie" &&
  release in $scopes &&
  (
    count(genres[lower(@) == "family"]) > 0 ||
    lower(title) match "*christmas*"
  )
] | order(releaseDate desc)[0...$limit]{
  _id,
  tmdbId,
  title,
  slug,
  releaseDate,
  release,
  poster { asset->{ url } },
  backdrop { asset->{ url } },
  voteAverage,
  voteCount,
  subheading,
  "youtubeTrailerId": coalesce(
    trailers[lower(site)=="youtube" && (lower(type)=="trailer" || lower(type)=="teaser")][0].key,
    videos[lower(site)=="youtube" && (lower(type)=="trailer" || lower(type)=="teaser")][0].key,
    videos[lower(platform)=="youtube" && (lower(category)=="trailer" || lower(category)=="teaser")][0].key
  )
}`

  const results = await sanityClient.fetch(query, { scopes, limit })

  const normalized = results.map((doc: any) => ({
    id: doc.tmdbId ?? doc._id,
    title: doc.title,
    slug: doc.slug?.current,
    poster_path: doc.poster?.asset?.url ?? '',
    vote_average: doc.voteAverage,
    vote_count: doc.voteCount,
    release_date: doc.releaseDate,
    release: doc.release,
    _id: doc._id,
    youtubeTrailerId: doc.youtubeTrailerId ?? null,
  }))

  normalized.sort((a: any, b: any) =>
    String(b.release_date ?? '').localeCompare(String(a.release_date ?? ''))
  )

  return { data: { items: normalized, results: normalized, total_pages: 1 } }
}

// get movies by genre
export const getBlueFoxSanityByGenre = async (
  genre: string,                            // e.g., "Horror"
  scope: 'domestic' | 'international' | 'both' = 'both',
  limit = 30
) => {
  const scopes = scopesFor(scope)

  const query = `*[
  _type == "bfe_master_movie" &&
  release in $scopes &&
  count(genres[lower(@) == lower($genre)]) > 0
] | order(releaseDate desc)[0...$limit]{
  _id,
  tmdbId,
  title,
  slug,
  releaseDate,
  release,
  poster { asset->{ url } },
  backdrop { asset->{ url } },
  voteAverage,
  voteCount,
  subheading,
  "youtubeTrailerId": coalesce(
    trailers[lower(site)=="youtube" && (lower(type)=="trailer" || lower(type)=="teaser")][0].key,
    videos[lower(site)=="youtube" && (lower(type)=="trailer" || lower(type)=="teaser")][0].key,
    videos[lower(platform)=="youtube" && (lower(category)=="trailer" || lower(category)=="teaser")][0].key
  )
}`


  const results = await sanityClient.fetch(query, { genre, scopes, limit })

  const normalized = results.map((doc: any) => ({
    id: doc.tmdbId ?? doc._id,
    title: doc.title,
    slug: doc.slug?.current,
    poster_path: doc.poster?.asset?.url ?? '',
    vote_average: doc.voteAverage,
    vote_count: doc.voteCount,
    release_date: doc.releaseDate,
    release: doc.release,
    _id: doc._id,
    youtubeTrailerId: doc.youtubeTrailerId ?? null,
  }))

  normalized.sort((a: any, b: any) =>
    String(b.release_date ?? '').localeCompare(String(a.release_date ?? ''))
  )

  return { data: { items: normalized, results: normalized, total_pages: 1 } }
}

export const getBlueFoxMasterSanityData = async (scope: ReleaseScope = 'both') => {
  const scopes = scopesFor(scope)

  const query = `*[_type == "bfe_master_movie" && release in $scopes]
    | order(subheading.isHeroMovie desc, releaseDate desc){
      _id,
      tmdbId,
      title,
      slug,
      releaseDate,
      release,                                 // 👈 include your release flag
      poster { asset->{ url } },
      backdrop { asset->{ url } },
      voteAverage,
      voteCount,
      subheading,

      "youtubeTrailerId": coalesce(
        trailers[lower(site)=="youtube" && (lower(type)=="trailer" || lower(type)=="teaser")][0].key,
        videos[lower(site)=="youtube" && (lower(type)=="trailer" || lower(type)=="teaser")][0].key,
        videos[lower(platform)=="youtube" && (lower(category)=="trailer" || lower(category)=="teaser")][0].key
      )
    }`

  const results = await sanityClient.fetch(query, { scopes })

  const normalized = results.map((doc: any) => ({
    id: doc.tmdbId ?? doc._id,
    title: doc.title,
    slug: doc.slug?.current,
    poster_path: doc.poster?.asset?.url ?? '',
    backdrop_path: doc.backdrop?.asset?.url ?? '',
    vote_average: doc.voteAverage,
    vote_count: doc.voteCount,
    release_date: doc.releaseDate, // used for sorting & display
    release: doc.release as ReleaseScope, // 'domestic' | 'international' | 'both'
    _id: doc._id,
    youtubeTrailerId: doc.youtubeTrailerId ?? null,
  }))

  // Already ordered by GROQ, but keep this if you transform later:
  normalized.sort((a: { release_date: any }, b: { release_date: any }) => (b.release_date ?? '').localeCompare(a.release_date ?? ''))

  return {
    data: { items: normalized, results: normalized, total_pages: 1 },
  }
}



export const getHeroMasterMovieFromSanity = async (limit = 10) => {
  const query = `*[
    _type == "bfe_master_movie" &&
    !(_id in path('drafts.**')) &&             // exclude drafts
    (
      isHeroMovie == true ||                   // top-level toggle
      subheading.isHeroMovie == true           // older/nested toggle (back-compat)
    )
  ]
  | order(coalesce(subheading.heroPosition, 999) asc, _updatedAt desc)[0...$limit]{
    _id,
    tmdbId,
    title,
    slug,
    releaseDate,
    overview,
    tagline,
    poster,
    backdrop,
    logo,
    voteAverage,
    voteCount,
    subheading,
    isHeroMovie,
    _updatedAt
  }`;

  const results = await sanityClientFresh.fetch(query, { limit });
  if (!results?.length) return [];

  return results.map((r: any) => ({
    id: r.tmdbId,
    title: r.title,
    overview: r.overview,
    // use urlFor so crop/hotspot are respected
    backdrop_path: r.backdrop ? urlFor(r.backdrop).url() : "",
    poster_path: r.poster ? urlFor(r.poster).url() : "",
    // 👇 NEW: Sanity-curated logo URL (prefer this in the UI)
    sanity_logo_url: r.logo ? urlFor(r.logo).url() : null,
    vote_average: r.voteAverage,
    vote_count: r.voteCount,
    release_date: r.releaseDate,
    slug: r.slug?.current,
    _id: r._id,
    subheading: r.subheading,
    isHeroMovie: r.isHeroMovie ?? r.subheading?.isHeroMovie ?? null,
    heroPosition: r.subheading?.heroPosition ?? null,
  }));
};


export const getMasterMovieFromSanityBySlug = async (slug: string | undefined) => {
  const query = `*[_type == "bfe_master_movie" && slug.current == $slug][0]{
      _id,
      "tmdb_id": tmdbId,
      title,
      slug,
      "release_date": releaseDate,
      release,
      overview,
      tagline,
      poster { asset->{url} },
      backdrop { asset->{url} },
      voteAverage,
      voteCount,
      originalLanguage,
      spokenLanguages,
      genres,
      showInDigitalOnDemand,
      status,
      trailers[] {
        key,
        site,
        name,
        type
      },
      streamingLinks[]{  // 👈 add this
        name,
        type,
        web_url,
        region,
        format,
        price,
        isOfficial,
        priority,
        source_id
      },
      ${CAST_CREW_PROJECTION}
    }`


  const result = await sanityClient.fetch(query, { slug })

  if (!result) return null

  return {
    id: result.tmdbId,
    tmdb_id: result.tmdb_id,
    title: result.title,
    slug: result.slug?.current,
    release_date: result.release_date,
    release: result.release,
    overview: result.overview,
    tagline: result.tagline,
    poster_path: result.poster ? urlFor(result.poster).url() : '',
    backdrop_path: result.backdrop ? urlFor(result.backdrop).url() : '',
    vote_average: result.voteAverage,
    vote_count: result.voteCount,
    genres: result.genres,
    original_language: result.originalLanguage,
    spoken_languages: result.spokenLanguages,
    trailers: result.trailers || [],
    streamingLinks: result.streamingLinks || [],
    showInDigitalOnDemand: result.showInDigitalOnDemand,
    status: result.status,
    cast: normalizeSanityCast(result.cast),
    crew: normalizeSanityCrew(result.crew),
  }

}


/// Master canada endpoint from sanity


export const getBlueFoxCanadaSanityData = async () => {
  const query = `*[_type == "bfe_movie"]
    | order(subheading.isHeroMovie desc, releaseDate desc){
      _id,
      tmdbId,
      title,
      slug,
      releaseDate,
      poster { asset->{ url } },
      backdrop { asset->{ url } },
      voteAverage,
      voteCount,
      subheading,

      // 👇 Pick the first YouTube Trailer/Teaser key
      "youtubeTrailerId": coalesce(
        trailers[lower(site)=="youtube" && (lower(type)=="trailer" || lower(type)=="teaser")][0].key,
        videos[lower(site)=="youtube" && (lower(type)=="trailer" || lower(type)=="teaser")][0].key,
        videos[lower(platform)=="youtube" && (lower(category)=="trailer" || lower(category)=="teaser")][0].key
      )
    }`

  const results = await sanityClient.fetch(query)

  const normalized = results.map((doc: any) => ({
    id: doc.tmdbId ?? doc._id,
    title: doc.title,
    poster_path: doc.poster ? urlFor(doc.poster).url() : '',
    vote_average: doc.voteAverage,
    vote_count: doc.voteCount,
    release_date: doc.releaseDate,
    slug: doc.slug?.current,
    _id: doc._id,
    // 👇 IMPORTANT: include the trailer id
    youtubeTrailerId: doc.youtubeTrailerId ?? null,
  }))

  return {
    data: {
      items: normalized,
      results: normalized,
      total_pages: 1,
    },
  }
}

// Add this new function to get the hero movie from Sanity
export const getHeroMovieFromSanity = async (limit = 10) => {
  const query = `*[
    _type == "bfe_movie" &&
    !(_id in path('drafts.**')) &&             // exclude drafts
    (
      isHeroMovie == true ||                   // top-level toggle
      subheading.isHeroMovie == true           // older/nested toggle (back-compat)
    )
  ]
  | order(coalesce(subheading.heroPosition, 999) asc, _updatedAt desc)[0...$limit]{
    _id,
    tmdbId,
    title,
    slug,
    releaseDate,
    overview,
    tagline,
    poster,
    backdrop,
    logo,
    voteAverage,
    voteCount,
    subheading,
    isHeroMovie,
    _updatedAt
  }`;

  const results = await sanityClientFresh.fetch(query, { limit });
  if (!results?.length) return [];

  return results.map((r: any) => ({
    id: r.tmdbId,
    title: r.title,
    overview: r.overview,
    // use urlFor so crop/hotspot are respected
    backdrop_path: r.backdrop ? urlFor(r.backdrop).url() : "",
    poster_path: r.poster ? urlFor(r.poster).url() : "",
    // 👇 NEW: Sanity-curated logo URL (prefer this in the UI)
    sanity_logo_url: r.logo ? urlFor(r.logo).url() : null,
    vote_average: r.voteAverage,
    vote_count: r.voteCount,
    release_date: r.releaseDate,
    slug: r.slug?.current,
    _id: r._id,
    subheading: r.subheading,
    isHeroMovie: r.isHeroMovie ?? r.subheading?.isHeroMovie ?? null,
    heroPosition: r.subheading?.heroPosition ?? null,
  }));
};



// Get each movie slug from Sanity
export const getMovieFromSanityBySlug = async (slug: string | undefined) => {
  const query = `*[_type == "bfe_movie" && slug.current == $slug][0]{
    _id,
    "tmdb_id": tmdbId,
    title,
    slug,
    "release_date": releaseDate,
    release,
    overview,
    tagline,
    poster { asset->{url} },
    backdrop { asset->{url} },
    voteAverage,
    voteCount,
    originalLanguage,
    spokenLanguages,
    genres,
    showInDigitalOnDemand,
    status,
    trailers[] {
      key,
      site,
      name,
      type
    },
    streamingLinks[]{  // 👈 add this
        name,
        type,
        web_url,
        region,
        format,
        price,
        isOfficial,
        priority,
        source_id
      },
      ${CAST_CREW_PROJECTION}
  }`

  const result = await sanityClient.fetch(query, { slug })

  if (!result) return null

  return {
    id: result.tmdbId,
    tmdb_id: result.tmdb_id,
    title: result.title,
    slug: result.slug?.current,
    release_date: result.release_date,
    release: result.release,
    overview: result.overview,
    tagline: result.tagline,
    poster_path: result.poster ? urlFor(result.poster).url() : '',
    backdrop_path: result.backdrop ? urlFor(result.backdrop).url() : '',
    vote_average: result.voteAverage,
    vote_count: result.voteCount,
    genres: result.genres,
    original_language: result.originalLanguage,
    spoken_languages: result.spokenLanguages,
    trailers: result.trailers || [],
    streamingLinks: result.streamingLinks || [],
    showInDigitalOnDemand: result.showInDigitalOnDemand,
    status: result.status,
    cast: normalizeSanityCast(result.cast),
    crew: normalizeSanityCrew(result.crew),
  }
}

// This is sanity data and client requests and returns all data from Sanity
export const getRedHoundSanityData = async () => {
  const query = `*[_type == "redhound_movie"] | order(releaseDate desc){
    _id,
    tmdbId,
    title,
    slug,
    releaseDate,
    poster { asset->{url} },
    backdrop { asset->{url} },
    voteAverage,
    voteCount,
    "youtubeTrailerId": coalesce(
      trailers[lower(site)=="youtube" && (lower(type)=="trailer" || lower(type)=="teaser")][0].key,
      videos[lower(site)=="youtube" && (lower(type)=="trailer" || lower(type)=="teaser")][0].key,
      videos[lower(platform)=="youtube" && (lower(category)=="trailer" || lower(category)=="teaser")][0].key
    )
  }`

  const results = await sanityClient.fetch(query)

  // Normalize for MovieCard compatibility
  const normalized = results.map((doc: any) => ({
    id: doc.tmdbId,
    title: doc.title,
    // poster_path: doc.poster?.asset?.url || '',
    poster_path: doc.poster ? urlFor(doc.poster).url() : '',
    vote_average: doc.voteAverage,
    vote_count: doc.voteCount,
    release_date: doc.releaseDate,
    slug: doc.slug?.current,
    _id: doc._id,
    youtubeTrailerId: doc.youtubeTrailerId ?? null,
  }))

  return {
    data: {
      items: normalized,
      results: normalized,
      total_pages: 1,
    },
  }
}

export const getRedHoundMovieFromSanityBySlug = async (slug: string | undefined) => {
  const query = `*[_type == "redhound_movie" && slug.current == $slug][0]{
    _id,
    "tmdb_id": tmdbId,
    title,
    slug,
    "release_date": releaseDate,
    release,
    overview,
    tagline,
    poster { asset->{url} },
    backdrop { asset->{url} },
    voteAverage,
    voteCount,
    originalLanguage,
    spokenLanguages,
    genres,
    showInDigitalOnDemand,
    status,
    trailers[] {
      key,
      site,
      name,
      type
    },
    streamingLinks[]{
        name,
        type,
        web_url,
        region,
        format,
        price,
        isOfficial,
        priority,
        source_id
      },
      ${CAST_CREW_PROJECTION}
  }`

  const result = await sanityClient.fetch(query, { slug })

  if (!result) return null

  return {
    id: result.tmdbId,
    tmdb_id: result.tmdb_id,
    title: result.title,
    slug: result.slug?.current,
    release_date: result.release_date,
    release: result.release,
    overview: result.overview,
    tagline: result.tagline,
    poster_path: result.poster ? urlFor(result.poster).url() : '',
    backdrop_path: result.backdrop ? urlFor(result.backdrop).url() : '',
    vote_average: result.voteAverage,
    vote_count: result.voteCount,
    genres: result.genres,
    original_language: result.originalLanguage,
    spoken_languages: result.spokenLanguages,
    trailers: result.trailers || [],
    streamingLinks: result.streamingLinks || [],
    showInDigitalOnDemand: result.showInDigitalOnDemand,
    status: result.status,
    cast: normalizeSanityCast(result.cast),
    crew: normalizeSanityCrew(result.crew),
  }
}

export const getTrending = async (type: string, payload: object) => {
  const response = await Api().get(`/trending/all/${type}`, { params: payload })
  return response
}

// MovieApi.ts
// export const getTrending = async (
//   mediaType: 'all' | 'movie' | 'tv',
//   timeWindow: 'day' | 'week',
//   payload: object
// ) => {
//   const response = await Api().get(`/trending/${mediaType}/${timeWindow}`, {
//     params: payload
//   })
//   return response
// }

export const getNewRelease = async (type: string, payload: IGetListPayload) => {
  const response = await Api().get(`/${type}/latest`, { params: payload })
  return response
}

export const getPopular = async (type: string, payload: IGetListPayload) => {
  const response = await Api().get(`/${type}/popular`, { params: payload })
  return response
}

export const getTopRated = async (type: string, payload: IGetListPayload) => {
  const response = await Api().get(`/${type}/top_rated`, { params: payload })
  return response
}

export const getNowPlaying = async (payload: IGetListPayload) => {
  const response = await Api().get('/movie/now_playing', { params: payload })
  return response
}

export const getUpcoming = async (payload: IGetListPayload) => {
  const response = await Api().get('/movie/upcoming', { params: payload })
  return response
}

export const getAiringToday = async (payload: IGetListPayload) => {
  const response = await Api().get('/tv/airing_today', { params: payload })
  return response
}

export const getOnTheAir = async (payload: IGetListPayload) => {
  const response = await Api().get('/tv/on_the_air', { params: payload })
  return response
}

export const getDetail = async (type: string, id: string, payload: IDetailPayload) => {
  const response = await Api().get(`/${type}/${id}`, { params: payload })
  return response
}

// export const getCredits = async (type: string, id: string, payload: IDetailPayload) => {
//   const response = await Api().get(`/${type}/${id}/credits`, { params: payload })
//   return response
// }

export const getCredits = (type: string, id: string | number, params: any) =>
  tmdb.get(`/${type}/${id}/credits`, { params });

export const getReviews = (type: string, id: string | number, params: any) =>
  tmdb.get(`/${type}/${id}/reviews`, { params });

export const getTrailers = async (type: string, id: string, payload: IDetailPayload) => {
  const response = await Api().get(`/${type}/${id}/videos`, { params: payload })
  return response
}

// export const getReviews = async (type: string, id: string, payload: IDetailPayload) => {
//   const response = await Api().get(`/${type}/${id}/reviews`, { params: payload })
//   return response
// }

export const getRecommendation = async (type: string, id: string, payload: IDetailPayload) => {
  const response = await Api().get(`/${type}/${id}/recommendations`, { params: payload })
  return response
}

export const searchFilm = async (payload: object) => {
  const response = await Api().get(`search/movie`, { params: payload })
  return response
}

export const searchTv = async (payload: object) => {
  const response = await Api().get(`search/tv`, { params: payload })
  return response
}

// Festivals from Sanity
export type Festival = {
  name: string
  city: string
  country: string
  start: string   // ISO yyyy-mm-dd
  end: string     // ISO yyyy-mm-dd
  website?: string
  badge?: 'Market' | 'Festival' | 'Both'
  notes?: string
}

export const fetchFestivalsFromSanity = async (): Promise<Festival[]> => {
  try {
    const query = `*[_type == "festival"] | order(start asc) {
      name,
      city,
      country,
      start,
      end,
      website,
      badge,
      notes
    }`

    const results = await sanityClient.fetch<Festival[]>(query)
    return results ?? []
  } catch (error) {
    console.error('Error fetching festivals from Sanity:', error)
    return []
  }
}
